/**
 * Sélecteur de cours SIMPLE pour un débutant : Pour qui ? → Quelle discipline ? → Quel jour ?
 * Ne montre que les cours ouverts aux débutants. Renvoie la même valeur `preferred_slot` qu'avant.
 */
import { useMemo, useState } from "react";
import { Baby, User, Users, Heart, Check } from "lucide-react";

const WHO = [
  { id: "enfant", label: "Enfant 3-6 ans", age: 5, Icon: Baby },
  { id: "jeune", label: "Jeune 8-13 ans", age: 10, Icon: Users },
  { id: "ado", label: "Ado 14-17 ans", age: 15, Icon: User },
  { id: "adulte", label: "Adulte 18 +", age: 25, Icon: User },
];
const DISCIPLINES = [
  { id: "kick", label: "Kick-Boxing / Muay-Thaï", hint: "Cardio, frappes, énergie", match: (d) => /kick|muay/i.test(d) },
  { id: "bjj", label: "Jiu-jitsu brésilien", hint: "Combat au sol, technique", match: (d) => /brésilien|bresilien|bjj/i.test(d) },
  { id: "canryu", label: "Autodéfense (Jiu-jitsu Can Ryu)", hint: "Se défendre, confiance", match: (d) => /can ryu/i.test(d) },
];
const DAY_ORDER = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const beginnerOk = (c) => !/verte|mauve|intermédiaire|avancé/i.test(`${c.niveau_ceinture || ""} ${c.titre_du_cours || ""}`);
const hour = (h) => (h || "").replace(":00", " h").replace(":", " h ");
const isPkh = (c) => /powerkick/i.test(c.titre_du_cours || "");

export default function CoursPicker({ days = [], value, onChange }) {
  const [who, setWho] = useState("");
  const [disc, setDisc] = useState("");

  const all = useMemo(() => {
    const out = [];
    days.forEach((d) => (d.cours || []).forEach((c) => out.push({ ...c, jour: d.jour })));
    return out.filter(beginnerOk);
  }, [days]);

  const age = WHO.find((w) => w.id === who)?.age;
  const forAge = all.filter((c) => (!isPkh(c) || who === "adulte") && (age == null || ((c.age_min == null || c.age_min <= age) && (c.age_max == null || c.age_max >= age))));
  const discs = DISCIPLINES.filter((D) => forAge.some((c) => D.match(c.discipline || "")));
  const D = DISCIPLINES.find((x) => x.id === disc);
  const slots = D ? forAge.filter((c) => D.match(c.discipline || "")) : [];
  const byDay = DAY_ORDER.map((j) => ({ jour: j, cours: slots.filter((c) => c.jour === j) })).filter((d) => d.cours.length);
  const valueOf = (c) => `${c.jour} ${c.heure_debut} — ${c.titre_du_cours}${c.discipline ? " (" + c.discipline + ")" : ""}`;

  const pill = (active) => `px-3 py-2 rounded-lg border text-sm text-left transition ${active ? "border-amber-500 bg-amber-500/10 text-white" : "border-zinc-700 bg-zinc-950 text-zinc-300 hover:border-zinc-500"}`;

  return (
    <div className="space-y-4" data-testid="cours-picker">
      <div>
        <div className="text-xs font-semibold text-zinc-400 mb-1.5">1. C'est pour qui ?</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {WHO.map((w) => (
            <button type="button" key={w.id} onClick={() => { setWho(w.id); setDisc(""); onChange(null); }} data-testid={`picker-who-${w.id}`} className={`${pill(who === w.id)} flex items-center gap-2`}>
              <w.Icon className="w-4 h-4 text-amber-400 shrink-0" /> {w.label}
            </button>
          ))}
        </div>
      </div>

      {who && (
        <div>
          <div className="text-xs font-semibold text-zinc-400 mb-1.5">2. Quelle discipline ?</div>
          <div className="grid sm:grid-cols-3 gap-2">
            {discs.map((d) => (
              <button type="button" key={d.id} onClick={() => { setDisc(d.id); onChange(null); }} data-testid={`picker-disc-${d.id}`} className={pill(disc === d.id)}>
                <div className="font-semibold">{d.label}</div>
                <div className="text-xs text-zinc-500">{d.hint}</div>
              </button>
            ))}
            {discs.length === 0 && <div className="text-xs text-zinc-500 sm:col-span-3">Aucun cours débutant pour ce groupe d'âge — laisse-nous un message, on te guide.</div>}
          </div>
        </div>
      )}

      {disc && (
        <div>
          <div className="text-xs font-semibold text-zinc-400 mb-1.5">3. Quel jour te convient ? <span className="text-zinc-500 font-normal">(tu pourras changer)</span></div>
          <div className="space-y-2">
            {byDay.map((d) => (
              <div key={d.jour} className="flex flex-wrap items-center gap-2">
                <span className="w-20 text-sm font-semibold text-white">{d.jour}</span>
                {d.cours.map((c) => {
                  const v = valueOf(c); const active = value === v;
                  return (
                    <button type="button" key={c.id} onClick={() => onChange({ value: v, jour: c.jour })} data-testid={`picker-slot-${c.id}`}
                      className={`px-3 py-1.5 rounded-full border text-sm flex items-center gap-1.5 ${active ? "border-amber-500 bg-amber-500 text-black font-semibold" : "border-zinc-700 bg-zinc-950 text-zinc-200 hover:border-amber-500/60"}`}>
                      {active && <Check className="w-3.5 h-3.5" />} {hour(c.heure_debut)}
                      {isPkh(c) && <span className="inline-flex items-center gap-0.5 text-[10px] text-pink-300"><Heart className="w-3 h-3" /> femmes</span>}
                      {!isPkh(c) && c.titre_du_cours && !/muay|kick|bjj|autodéfense/i.test(c.titre_du_cours) && <span className="text-[10px] text-zinc-400">· {c.titre_du_cours}</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {value && <div className="text-xs text-emerald-400 flex items-center gap-1.5" data-testid="picker-summary"><Check className="w-3.5 h-3.5" /> Ton essai : {value}</div>}
    </div>
  );
}
